/**
 * Layout Component
 * Main application layout with header and navigation
 */

import { useEffect } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { Activity, AlertTriangle, BarChart3, LogOut, User, Wifi, WifiOff } from 'lucide-react';
import { useAlertStore, getConnectedSourceCount } from '@/stores/alert-store';
import { useAuthStore } from '@/stores/auth-store';
import { useWebSocket } from '@/hooks/use-websocket';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  // Initialize WebSocket connection
  useWebSocket();

  // Check auth on mount
  const checkAuth = useAuthStore((s) => s.checkAuth);
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const { connected, connecting, sources, error } = useAlertStore();
  const connectedCount = getConnectedSourceCount(sources);
  const totalSources = Object.keys(sources).length;

  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const routerState = useRouterState();
  const isFullBleed = routerState.location.pathname === '/alerts';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-14 items-center">
          {/* Logo */}
          <div className="mr-4 flex items-center space-x-2">
            <Activity className="h-6 w-6 text-primary" />
            <span className="font-bold">Pivots Flow</span>
          </div>

          {/* Navigation */}
          <nav className="flex items-center space-x-6 text-sm font-medium">
            <Link
              to="/"
              className="transition-colors hover:text-foreground/80 text-foreground"
              activeProps={{ className: 'text-primary' }}
            >
              <span className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Dashboard
              </span>
            </Link>
            <Link
              to="/alerts"
              className="transition-colors hover:text-foreground/80 text-foreground"
              activeProps={{ className: 'text-primary' }}
            >
              <span className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Alerts
              </span>
            </Link>
          </nav>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Connection Status */}
          <div className="flex items-center space-x-4">
            {/* Source count */}
            <div className="text-xs text-muted-foreground">
              {connectedCount}/{totalSources} sources
            </div>

            {/* Connection indicator */}
            <div
              className={cn(
                'flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium',
                connected
                  ? 'bg-green-500/10 text-green-500'
                  : connecting
                  ? 'bg-yellow-500/10 text-yellow-500'
                  : 'bg-red-500/10 text-red-500'
              )}
            >
              {connected ? (
                <>
                  <Wifi className="h-3 w-3" />
                  Connected
                </>
              ) : connecting ? (
                <>
                  <Wifi className="h-3 w-3 animate-pulse" />
                  Connecting...
                </>
              ) : (
                <>
                  <WifiOff className="h-3 w-3" />
                  Disconnected
                </>
              )}
            </div>

            {/* Error indicator */}
            {error && (
              <div className="text-xs text-red-500" title={error}>
                Error
              </div>
            )}

            {/* Auth controls */}
            {user ? (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <User className="h-3 w-3 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">{user.email}</span>
                  <Badge variant={user.tier === 'lifetime' ? 'default' : user.tier === 'pro' ? 'secondary' : 'outline'} className="text-[10px] px-1.5 py-0">
                    {user.tier}
                  </Badge>
                </div>
                <button
                  onClick={() => logout()}
                  className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <LogOut className="h-3 w-3" />
                  Logout
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="text-xs text-primary hover:underline"
              >
                Sign in
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Main content — dashboard is full-bleed, other pages use container */}
      <main className={isFullBleed ? '' : 'container py-6'}>{children}</main>
    </div>
  );
}
